import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {MIN_MAX_SIO, SIOS_DATA} from '../Model/Models';
import HandleChange from '../../../../../Helpers/HandleChange';
import { AdvanceSearchByFilters, CreateHidSioRecord } from '../../../../../reduxToolkit/HID/HidApi';
import { errorMsg, isNumber } from "../../../../../Apis/functions";
import { TABLES } from "../../../../../Apis/Tables";
import { SearchByFilters } from '../../../../../reduxToolkit/Search/SearchApi';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @Description
 * Hook-based component that handles the creation of SIO (Serial Input/Output) records
 * related to a specific HID controller. It validates form fields, manages state,
 * and triggers API calls for record creation.
 */
const CreateSiosApp = ({ onHide, controllerId = null, setModified }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { advanceSearchByFilters } = useSelector((state) => state.HidSlice);

    // Local states
    const [emptyFields, setEmptyFields] = useState(false);
    const [isValidPortNumber, setIsValidPortNumber] = useState(true);
    const [sios, setSios] = useState({ ...SIOS_DATA, hidId: controllerId });

    const { handleChange } = HandleChange({ model: sios, setModel: setSios });

    /**
     * Loads HID controllers if no controllerId is provided.
     */
    useEffect(() => {
        if (!controllerId)
            dispatch(AdvanceSearchByFilters({ option: TABLES.HID, body: {} }));
    }, [controllerId]);

    /**
     * Validates that the port number is numeric and within the allowed range.
     */
    useEffect(() => {
        if (sios.portNumber !== null && sios.portNumber !== "") {
            const portNumber = parseInt(sios.portNumber);
            if (isNumber(portNumber) && portNumber > MIN_MAX_SIO.PORT_NUMBER.start && portNumber <= MIN_MAX_SIO.PORT_NUMBER.end) {
                setIsValidPortNumber(true);
                return;
            }
            setIsValidPortNumber(false);
        }
    }, [sios.portNumber]);

    /**
     * Handles form submission for creating a new SIO record.
     * Validates input fields and calls the create API.
     */
    const handleSubmit = () => {
        if (!sios.isValid() || !isValidPortNumber) {
            if (!sios.isValid()) setEmptyFields(true);
            return;
        }

        const body = sios.toAddEdit();
        dispatch(CreateHidSioRecord(body)).then(
            ({ payload: { data: { success, data, code } } }) => {
                if (success === true) {
                    setModified(true);
                    toast.success(t('has_been_created_successfully'));
                    resetValues();
                    onHide();
                } else errorMsg(code, { portNumber: sios.portNumber });
            },
        );
    };

    /**
     * Resets SIO form values to their initial state.
     */
    const resetValues = () => {
        setSios({ ...SIOS_DATA, hidId: controllerId });
    };

    // Return exposed values and handlers
    return {
        t,
        emptyFields,
        isValidPortNumber,
        resetValues,
        handleSubmit,
        sios,
        handleChange,
        controller: advanceSearchByFilters,
    };
};

export default CreateSiosApp;
